import { Alert, Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react"
import { useNavigate, useParams } from "react-router-dom";
import { SOFulfillmentAPI, SOAPI, CustomerAPI } from "@/apis/sales";
import { Number } from "@/components/Numeric";
import Reference from "@/components/Reference";
import { useCallback, useEffect, useState } from "react";
import { FileUtil } from "@/utils";
import { FulfillmentStatusDef, FulfillmentStatus, SOStatus } from "@/enums/sales";
import { EnumLabel } from "@/components/Enum";
import GLImpactButton from "@/components/GLImpactButton";
import SaleOrderLink from "@/components/SaleOrderLink";
import SendEmailButton from "@/components/SendEmailButton";
import _ from "lodash";

export const SOFulfillmentInfo = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [info, setInfo] = useState({});
    const [so, setSO] = useState();
    const [printLoading, setPrintLoading] = useState(false);

    const back = () => {
        navigate("/workspace/sales/so-fulfillment");
    }

    const getInfo = useCallback(async () => {
        const values = await SOFulfillmentAPI.get(params.fulfillmentId);
        setInfo(values);

        const so = await SOAPI.get(values.soId);
        setSO(so);
    }, [params]);

    const voidFulfillment = async () => {
        try {
            await SOFulfillmentAPI.voidFulfillment(params.fulfillmentId);
            Message.success("Fulfillment voided successfully");
            getInfo();
        } catch (err) {
            Message.error(err.message);
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo])

    return (
        <>
            <Alert style={{ marginBottom: 10 }} content="Psst: Items from Pick Orders can not be changed on fulfillment page. To make any changes of items from Pick Orders, go back to Pick Order, make the changes, and refulfill the Pick Order." />
            <Card title={
                <Space>
                    <span>SO Fulfillment #{info.soFulfillmentNo}</span>
                    <Tag><EnumLabel enumObj={FulfillmentStatusDef} value={info.status} /></Tag>
                </Space>
            } extra={
                <Space>
                    {[SOStatus.Created, SOStatus.Issued, SOStatus.Picked, SOStatus.Partial, SOStatus.PartialFulfilledInvoiced, SOStatus.ShortClosed, SOStatus.Fulfilled, SOStatus.FulfilledPartiallyInvoiced].includes(so?.status) && info.status !== FulfillmentStatus.Voided &&
                        <Button type="primary" onClick={() => {
                            navigate(`/workspace/sales/invoice/so/${so.id}?fulfillmentId=${info.id}`);
                        }}>Create Invoice</Button>
                    }
                    {info.status !== FulfillmentStatus.Voided &&
                        <Button type="primary" onClick={() => navigate(`/workspace/sales/so-fulfillment/${params.fulfillmentId}/edit`)}>Edit</Button>
                    }
                    {info.status !== FulfillmentStatus.Voided &&
                        <Popconfirm title="Are you sure you want to void fulfillment?"
                            onOk={() => voidFulfillment()}>
                            <Button type="primary" status="danger">Void</Button>
                        </Popconfirm>
                    }
                    {info.status !== FulfillmentStatus.Voided &&
                        <GLImpactButton type="3" from={params.fulfillmentId} />
                    }
                    {info.status !== FulfillmentStatus.Voided &&
                        <Button type="primary" onClick={async () => {
                            setPrintLoading(true);
                            try {
                                const res = await SOFulfillmentAPI.print(info.id);
                                FileUtil.download(res.data, `${info.soFulfillmentNo}.pdf`);
                            } catch (error) {
                                Message.error(error.message);
                            } finally {
                                setPrintLoading(false);
                            }
                        }} loading={printLoading}>Print</Button>
                    }
                    {info.status !== FulfillmentStatus.Voided &&
                        <SendEmailButton getEmailContent={async () => {
                            return await SOFulfillmentAPI.getEmail(info.id);
                        }} getToList={async () => {
                            const customer = await CustomerAPI.get(so.customerId);
                            return customer.contacts;
                        }} sendEmail={async (val) => {
                            await SOFulfillmentAPI.sendEmail(info.id, val);
                        }} />
                    }
                    <Button onClick={() => back()}>Back</Button>
                </Space>
            }>
                <Descriptions colon=":"
                    data={[{
                        label: "SO#",
                        value: <SaleOrderLink soId={info.soId}>{info.soNo}</SaleOrderLink>
                    }, {
                        label: "SO Due Date",
                        value: info.soDueDate
                    }, {
                        label: "SO Creation Date",
                        value: info.soCreateDate
                    }, {
                        label: "Pick Order#",
                        value: _.join(_.map(info.pickOrders, "pickOrderNo"), ", ")
                    }, {
                        label: "Fulfillment Date",
                        value: info.fulfillmentDate
                    }, {
                        label: "Reference",
                        value: <Reference value={info.references} />,
                    }]} />

                <Table rowKey="id" columns={[
                    {
                        title: "Line#", render: (col, record, index) => {
                            return <span>{index + 1}</span>
                        }
                    },
                    { title: "Item Number", dataIndex: "itemNo" },
                    { title: "Item Name", dataIndex: "itemName" },
                    { title: "Description", dataIndex: "description" },
                    { title: "SO Qty", render: (col, record) => <Number value={record.soQty} /> },
                    { title: "Qty Fulfilled", render: (col, record) => <Number value={record.qtyFulfilled} /> },
                    { title: "UOM", dataIndex: "uom" },
                    { title: "Warehouse", dataIndex: "warehouseName" },
                    { title: "Location", dataIndex: "locationName" },
                    { title: "Lot#", dataIndex: "lot" },
                    { title: "Expiration", dataIndex: "expiredDate" },
                ]}
                    data={info.items}
                />
            </Card >
        </>
    )
}
